import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Catalogo.css';
import './GestaoCatalogo.css';

const norm = s => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const fmt = n => Number(n || 0).toLocaleString('pt-BR');
const NIVEL = { 2: 'Departamento ERP', 4: 'Categoria', 6: 'Subcategoria' };

// Árvore de classes (N1 › N2 › N3) achatada num Map cod → nó, pra consultas rápidas.
function indexar(arvore) {
  const m = new Map();
  const visita = nos => nos.forEach(n => { m.set(n.cod, n); if (n.filhos) visita(n.filhos); });
  visita(arvore);
  return m;
}
// Algum nível acima (ou a própria classe) está escondido?
const ancestralOculto = (idx, cod) => [2, 4].filter(n => n < cod.length).map(n => idx.get(cod.slice(0, n))).find(a => a?.oculto);

/**
 * Seletor das classes do ERP que compõem um departamento. Marcar um nível de cima
 * já cobre tudo que está dentro (os de baixo aparecem marcados e travados).
 */
function SeletorClasses({ arvore, idx, sel, onChange, catAtual }) {
  const [abertos, setAbertos] = useState(() => new Set());
  const [q, setQ] = useState('');
  const termo = norm(q);
  const coberto = cod => sel.some(s => s !== cod && cod.startsWith(s));
  const alternar = cod => {
    if (sel.includes(cod)) onChange(sel.filter(s => s !== cod));
    else onChange([...sel.filter(s => !s.startsWith(cod)), cod]);
  };
  const casa = n => !termo || norm(`${n.cod} ${n.nome} ${n.erp}`).includes(termo) || (n.filhos || []).some(casa);
  const expandido = cod => !!termo || abertos.has(cod);
  const abrir = cod => setAbertos(s => { const x = new Set(s); x.has(cod) ? x.delete(cod) : x.add(cod); return x; });

  const linha = (n, nivel) => {
    const outros = n.departamentos.filter(d => d.cat !== catAtual);
    const temFilhos = (n.filhos || []).length > 0;
    const trav = coberto(n.cod);
    return (
      <div key={n.cod}>
        <div className={`gc-sel-linha n${nivel}`}>
          {temFilhos
            ? <button type="button" className="gc-exp" onClick={() => abrir(n.cod)} aria-label="Expandir"><i className={`bi bi-chevron-${expandido(n.cod) ? 'down' : 'right'}`} /></button>
            : <span className="gc-exp" />}
          <label className={trav ? 'trav' : ''}>
            <input type="checkbox" checked={trav || sel.includes(n.cod)} disabled={trav} onChange={() => alternar(n.cod)} />
            <span className="gc-sel-nome">{n.nome}</span>
            <span className="gc-cod">{n.cod}</span>
          </label>
          <span className="gc-qtd">{fmt(n.qtd)}</span>
          {outros.length > 0 && <span className="gc-em" title="Essa classe também aparece nesses departamentos">em {outros.map(d => d.nome).join(', ')}</span>}
        </div>
        {temFilhos && expandido(n.cod) && n.filhos.filter(casa).map(f => linha(f, nivel + 1))}
      </div>
    );
  };

  return (
    <div>
      <div className="gc-sel-escolhidas">
        {sel.length === 0 && <span className="text-muted" style={{ fontSize: '0.84rem' }}>Nenhuma classe escolhida ainda.</span>}
        {sel.map(c => (
          <span key={c} className="gc-tag">
            {idx.get(c)?.nome || c} <span className="gc-cod">{c}</span>
            <button type="button" onClick={() => alternar(c)} aria-label="Remover"><i className="bi bi-x" /></button>
          </span>
        ))}
      </div>
      <div className="cat-busca-wrap mb-2">
        <i className="bi bi-search" />
        <input className="cat-busca" type="search" placeholder="Buscar classe do ERP..." value={q} onChange={e => setQ(e.target.value)} autoComplete="off" />
      </div>
      <div className="gc-sel-lista">{arvore.filter(casa).map(n => linha(n, 1))}</div>
    </div>
  );
}

/**
 * Gestão do catálogo (permissão `catalogo_destaques`):
 *  1. Departamentos — as abas que o arquiteto vê. Cada uma junta classes do ERP de
 *     qualquer nível (ex.: "Metais e Louças" = 04 + 05). Criar, renomear, compor,
 *     ordenar, ligar/desligar e apagar, sem deploy.
 *  2. Classes do ERP — nome amigável (o ERP grava "PIAS E TANQUES") e esconder o que
 *     não deve aparecer. Nada disso altera o ERP: é tudo tabela do portal.
 */
export default function GestaoCatalogo() {
  const navigate = useNavigate();
  const [deps, setDeps] = useState([]);
  const [arvore, setArvore] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [modal, setModal] = useState(null); // { cat?, nome, classes, ativo }
  const [salvando, setSalvando] = useState(false);
  const [confirmaApagar, setConfirmaApagar] = useState(false);
  // árvore de classes
  const [abertos, setAbertos] = useState(() => new Set());
  const [buscaCl, setBuscaCl] = useState('');
  const [editando, setEditando] = useState(null); // { cod, nome }

  const carregar = useCallback(async () => {
    try {
      const [d, a] = await Promise.all([api.get('/catalogo/categorias'), api.get('/catalogo/classes')]);
      setDeps(d.data || []);
      setArvore(a.data || []);
    } catch (e) { setErro(e.response?.data?.error || 'Erro ao carregar'); }
    finally { setCarregando(false); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const idx = useMemo(() => indexar(arvore), [arvore]);
  const avisar = t => { setAviso(t); setTimeout(() => setAviso(''), 2500); };
  const falhou = (e, padrao) => setErro(e.response?.data?.error || padrao);
  const qtdDe = classes => classes.reduce((s, c) => s + (idx.get(c)?.qtd || 0), 0);

  // ---- Departamentos ----
  const mover = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= deps.length) return;
    const nova = [...deps];
    [nova[i], nova[j]] = [nova[j], nova[i]];
    setDeps(nova);
    try { await api.put('/catalogo/categorias/ordem', { cats: nova.map(d => d.cat) }); }
    catch (e) { falhou(e, 'Erro ao reordenar'); carregar(); }
  };
  const alternarAtivo = async d => {
    setErro('');
    setDeps(ds => ds.map(x => x.cat === d.cat ? { ...x, ativo: !d.ativo } : x));
    try { await api.put(`/catalogo/categorias/${d.cat}`, { ativo: !d.ativo }); avisar(d.ativo ? `${d.nome} desligado` : `${d.nome} liberado pros arquitetos`); carregar(); }
    catch (e) { falhou(e, 'Erro ao ligar/desligar'); carregar(); }
  };
  const verNoCatalogo = cat => {
    try {
      const est = JSON.parse(sessionStorage.getItem('catalogo_estado') || '{}');
      sessionStorage.setItem('catalogo_estado', JSON.stringify({ ...est, categoria: cat, n2Sel: '', subSel: [], pagina: 1, scrollY: 0 }));
    } catch { /* ignora */ }
    navigate('/catalogo');
  };

  const abrirModal = d => { setErro(''); setConfirmaApagar(false); setModal(d ? { ...d } : { nome: '', classes: [], ativo: false }); };
  const fecharModal = useCallback(() => { if (!salvando) setModal(null); }, [salvando]);
  const salvarModal = async () => {
    setSalvando(true); setErro('');
    try {
      const corpo = { nome: modal.nome, classes: modal.classes };
      if (modal.cat) await api.put(`/catalogo/categorias/${modal.cat}`, corpo);
      else await api.post('/catalogo/categorias', corpo);
      setModal(null);
      avisar(modal.cat ? 'Departamento salvo' : 'Departamento criado (desligado — confira em prévia antes de ligar)');
      await carregar();
    } catch (e) { falhou(e, 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };
  const apagar = async () => {
    setSalvando(true); setErro('');
    try { await api.delete(`/catalogo/categorias/${modal.cat}`); setModal(null); avisar('Departamento apagado'); await carregar(); }
    catch (e) { falhou(e, 'Erro ao apagar'); }
    finally { setSalvando(false); }
  };
  // ESC fecha + trava o scroll do fundo enquanto o modal está aberto
  useEffect(() => {
    if (!modal) return undefined;
    const onKey = e => { if (e.key === 'Escape') fecharModal(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [modal, fecharModal]);

  // ---- Classes ----
  const salvarClasse = async (cod, dados) => {
    setErro('');
    try { await api.put(`/catalogo/classes/${cod}`, dados); setEditando(null); avisar('Classe atualizada'); await carregar(); }
    catch (e) { falhou(e, 'Erro ao salvar a classe'); }
  };
  const termoCl = norm(buscaCl);
  const linhasClasses = useMemo(() => {
    const casaCl = n => !termoCl || norm(`${n.cod} ${n.nome} ${n.erp}`).includes(termoCl) || (n.filhos || []).some(casaCl);
    const out = [];
    const visita = (nos, nivel) => nos.filter(casaCl).forEach(n => {
      out.push({ n, nivel });
      if ((n.filhos || []).length && (termoCl || abertos.has(n.cod))) visita(n.filhos, nivel + 1);
    });
    visita(arvore, 1);
    return out;
  }, [arvore, abertos, termoCl]);
  const abrirCl = cod => setAbertos(s => { const x = new Set(s); x.has(cod) ? x.delete(cod) : x.add(cod); return x; });

  if (carregando) return <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>;

  const nomeOk = (modal?.nome || '').trim().length >= 2;

  return (
    <div className="gc">
      <h4 className="fw-bold mb-1">
        <i className="bi bi-diagram-3 me-2" style={{ color: 'var(--primary)' }} />
        Gestão do catálogo
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.88rem' }}>
        Organize como os produtos aparecem pros arquitetos. Tudo aqui vale na hora e
        <strong> não altera nada no ERP</strong> — são só nomes e agrupamentos do portal.
      </p>

      {erro && <div className="alert alert-danger">{erro}</div>}
      {aviso && <div className="gc-aviso"><i className="bi bi-check-circle-fill" />{aviso}</div>}

      {/* Etapa 1 — Departamentos */}
      <div className="card p-3 mb-4" style={{ borderRadius: 14 }}>
        <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
          <div>
            <div className="gc-etapa"><span>1</span>Departamentos</div>
            <div className="text-muted" style={{ fontSize: '0.82rem' }}>
              As abas do catálogo. Departamento desligado só aparece pra quem gerencia (marcado como “prévia”).
            </div>
          </div>
          <button type="button" className="esp-btn btn-sm-esp" onClick={() => abrirModal(null)}>
            <i className="bi bi-plus-lg me-1" />Novo departamento
          </button>
        </div>
        <div className="tabela-container">
          <table className="table table-hover table-arq mb-0 gc-tab">
            <thead>
              <tr><th style={{ width: 70 }}>Ordem</th><th>Departamento</th><th>Classes do ERP</th><th className="text-end">Produtos</th><th>Situação</th><th /></tr>
            </thead>
            <tbody>
              {deps.map((d, i) => (
                <tr key={d.cat}>
                  <td>
                    <div className="gc-ordem">
                      <button type="button" disabled={i === 0} onClick={() => mover(i, -1)} aria-label="Subir"><i className="bi bi-arrow-up" /></button>
                      <button type="button" disabled={i === deps.length - 1} onClick={() => mover(i, 1)} aria-label="Descer"><i className="bi bi-arrow-down" /></button>
                    </div>
                  </td>
                  <td className="fw-semibold">{d.nome}</td>
                  <td>
                    <div className="gc-tags">
                      {d.classes.map(c => <span key={c} className="gc-tag leve">{idx.get(c)?.nome || c} <span className="gc-cod">{c}</span></span>)}
                    </div>
                  </td>
                  <td className="text-end">{fmt(qtdDe(d.classes))}</td>
                  <td>
                    <button type="button" role="switch" aria-checked={d.ativo} className={`cat-switch ${d.ativo ? 'ativo' : ''}`} onClick={() => alternarAtivo(d)}>
                      <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>
                      {d.ativo ? 'Ligado' : 'Desligado'}
                    </button>
                  </td>
                  <td className="text-end text-nowrap">
                    <button type="button" className="esp-icone me-1" onClick={() => verNoCatalogo(d.cat)} title="Abrir no catálogo"><i className="bi bi-eye" /></button>
                    <button type="button" className="esp-btn-sec btn-sm-esp" onClick={() => abrirModal(d)}><i className="bi bi-pencil me-1" />Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Etapa 2 — Classes do ERP */}
      <div className="card p-3 mb-4" style={{ borderRadius: 14 }}>
        <div className="gc-etapa"><span>2</span>Nomes e visibilidade das classes</div>
        <div className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
          As 3 camadas de classe do ERP (departamento › categoria › subcategoria). Dê um nome amigável — é ele que o
          arquiteto vê nos filtros — ou esconda uma classe (esconder uma de cima esconde tudo dentro dela).
        </div>
        <div className="cat-busca-wrap mb-3" style={{ maxWidth: 420 }}>
          <i className="bi bi-search" />
          <input className="cat-busca" type="search" placeholder="Buscar por nome ou código..." value={buscaCl} onChange={e => setBuscaCl(e.target.value)} autoComplete="off" />
        </div>
        <div className="tabela-container">
          <table className="table table-arq mb-0 gc-tab gc-arvore">
            <thead>
              <tr><th>Classe</th><th className="text-end">Produtos</th><th>Aparece em</th><th>Visível</th></tr>
            </thead>
            <tbody>
              {linhasClasses.map(({ n, nivel }) => {
                const temFilhos = (n.filhos || []).length > 0;
                const pai = ancestralOculto(idx, n.cod);
                const escondida = n.oculto || !!pai;
                return (
                  <tr key={n.cod} className={`n${nivel} ${escondida ? 'escondida' : ''}`}>
                    <td>
                      <div className="gc-cl" style={{ paddingLeft: (nivel - 1) * 22 }}>
                        {temFilhos
                          ? <button type="button" className="gc-exp" onClick={() => abrirCl(n.cod)} aria-label="Expandir"><i className={`bi bi-chevron-${termoCl || abertos.has(n.cod) ? 'down' : 'right'}`} /></button>
                          : <span className="gc-exp" />}
                        {editando?.cod === n.cod ? (
                          <form className="gc-edit" onSubmit={e => { e.preventDefault(); salvarClasse(n.cod, { nome: editando.nome, oculto: n.oculto }); }}>
                            <input autoFocus value={editando.nome} maxLength={80} placeholder={n.erp}
                              onChange={e => setEditando({ ...editando, nome: e.target.value })}
                              onKeyDown={e => { if (e.key === 'Escape') setEditando(null); }} />
                            <button type="submit" className="esp-btn btn-sm-esp">Salvar</button>
                            <button type="button" className="esp-btn-sec btn-sm-esp" onClick={() => setEditando(null)}>Cancelar</button>
                          </form>
                        ) : (
                          <div>
                            <span className="gc-cl-nome">{n.nome}</span>
                            <button type="button" className="gc-lapis" onClick={() => setEditando({ cod: n.cod, nome: n.apelido || '' })} title="Renomear"><i className="bi bi-pencil" /></button>
                            <div className="gc-cl-erp">
                              {NIVEL[n.cod.length]} · <span className="gc-cod">{n.cod}</span> · ERP: {n.erp}
                              {n.apelido && <button type="button" className="gc-link" onClick={() => salvarClasse(n.cod, { nome: '', oculto: n.oculto })}>voltar pro nome do ERP</button>}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-end">{fmt(n.qtd)}</td>
                    <td>
                      {n.departamentos.length
                        ? n.departamentos.map(d => <span key={d.cat} className={`gc-dep ${d.ativo ? '' : 'off'}`}>{d.nome}</span>)
                        : <span className="gc-fora" title="Nenhum departamento inclui essa classe: os produtos dela não aparecem no catálogo">fora do catálogo</span>}
                    </td>
                    <td>
                      <button type="button" role="switch" aria-checked={!escondida} disabled={!!pai}
                        title={pai ? `Escondida porque “${pai.nome}” está escondida` : undefined}
                        className={`cat-switch ${escondida ? '' : 'ativo'}`}
                        onClick={() => salvarClasse(n.cod, { nome: n.apelido || '', oculto: !n.oculto })}>
                        <span className="cat-switch-track"><span className="cat-switch-thumb" /></span>
                        {escondida ? 'Escondida' : 'Visível'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {linhasClasses.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-4">Nenhuma classe encontrada.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal — criar/editar departamento */}
      {modal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title fw-bold">{modal.cat ? 'Editar departamento' : 'Novo departamento'}</h5>
                  <button type="button" className="btn-close" aria-label="Fechar" onClick={fecharModal} />
                </div>
                <div className="modal-body">
                  {erro && <div className="alert alert-danger py-2">{erro}</div>}
                  <label className="form-label fw-semibold" style={{ fontSize: '0.9rem' }}>Nome que o arquiteto vê</label>
                  <input className="form-control mb-3" value={modal.nome} maxLength={60} placeholder="Ex.: Metais e Louças"
                    onChange={e => setModal({ ...modal, nome: e.target.value })} />
                  <div className="d-flex align-items-baseline justify-content-between gap-2 mb-2">
                    <label className="form-label fw-semibold mb-0" style={{ fontSize: '0.9rem' }}>Classes do ERP que entram aqui</label>
                    <span className="text-muted" style={{ fontSize: '0.82rem' }}>{fmt(qtdDe(modal.classes))} produtos</span>
                  </div>
                  <SeletorClasses arvore={arvore} idx={idx} sel={modal.classes} catAtual={modal.cat}
                    onChange={classes => setModal({ ...modal, classes })} />
                  {!modal.cat && (
                    <div className="text-muted mt-3" style={{ fontSize: '0.8rem' }}>
                      <i className="bi bi-info-circle me-1" />O departamento nasce <strong>desligado</strong>: confira em prévia no catálogo e ligue quando estiver pronto.
                    </div>
                  )}
                </div>
                <div className="modal-footer justify-content-between">
                  <div>
                    {modal.cat && !confirmaApagar && (
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setConfirmaApagar(true)} disabled={salvando}>
                        <i className="bi bi-trash me-1" />Apagar
                      </button>
                    )}
                    {modal.cat && confirmaApagar && (
                      <span className="gc-confirma">
                        Apagar “{modal.nome}”? Os produtos continuam no ERP.
                        <button type="button" className="btn btn-danger btn-sm ms-2" onClick={apagar} disabled={salvando}>Sim, apagar</button>
                        <button type="button" className="btn btn-light btn-sm ms-1" onClick={() => setConfirmaApagar(false)} disabled={salvando}>Não</button>
                      </span>
                    )}
                  </div>
                  <div className="d-flex gap-2">
                    <button type="button" className="esp-btn-sec" onClick={fecharModal} disabled={salvando}>Cancelar</button>
                    <button type="button" className="esp-btn" onClick={salvarModal} disabled={salvando || !nomeOk || !modal.classes.length}>
                      {salvando ? 'Salvando…' : modal.cat ? 'Salvar alterações' : 'Criar departamento'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={fecharModal} />
        </>
      )}
    </div>
  );
}
